import React, { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  renderRoute,
  routeDocumentFinalEdits,
  routeDocumentFinalEditsWithContext,
  routeDocumentPrompt,
  routeDocumentRefine,
  routeDocumentRefineWithContext,
  routeDocumentSelection,
  routeReview,
  routeReviewWithDocument,
} from "@navigation/typedRoutes";
import ReviewView from "@view/document/Review";
import { useContentClient } from "@contexts/ContentClient.context";
import { useDocumentContext } from "@contexts/Document.context";
import { useLifecycleClient } from "@contexts/LifecycleClient.context";
import { useIoState } from "@src/utils/useIoState";
import { useIoOptionState } from "@utils/useIoOptionState";
import { useLoadingController } from "@contexts/Loading.context";
import { toast } from "sonner";
import { ok, type Ok } from "@ps-bridge/ok";
import type {
  ContentError,
  GenerateSectionQuestionsRequest,
  AppendSectionRequest,
  UploadSupplementalDocumentRequest,
} from "@ps-bridge/models/core/dataContracts";
import { useFilePicker } from "@src/hooks/useFilePicker";
import * as Option from "@utils/Option";
import { makeDocumentUploadFileName } from "@ps-bridge/models/brands/documentUploadFileName";
import { makeDocumentUploadFile } from "@ps-bridge/models/brands/documentUploadFile";
import { makeSupplementalRelation } from "@ps-bridge/models/brands/supplementalRelation";
import { makeDocumentID } from "@ps-bridge/models/brands/documentID";
import type { OptionTs, TaskTs } from "@ps-bridge/interop";
import type { StateUpdate } from "@ps-bridge/capabilitiesTypes";
import type { EmitOnRouteChange } from "@ps-bridge/capabilitiesTypes";
import type { DocumentID } from "@ps-bridge/models/brands/documentID";
import type { Section } from "@ps-bridge/models/core/dataContracts";
import { mkBlobHandler, mkNavigate, mkRouteBuilder, mkTaskRunner } from "@ps-bridge/capabilities";
import {
  type AppendSectionFetch,
  type DownloadDocumentAsBlobFetch,
  FetchDocumentByIdFetch,
  type GenerateSectionQuestionsFetch,
  type SelectUploadPayload,
  type UploadSupplementalDocumentFetch,
  type UploadSuccessToast,
} from "@ps-bridge/contracts/documentReview";
import type { DocumentReviewKernel, UploadSupplementalPayload } from "@ps-bridge/contracts/documentReview";
import { buildDocumentReviewViewProps } from "@ps-bridge/documentReview.program";
import {
  ioEffect,
  taskEitherFromEffect,
  taskMap,
} from "@utils/effects/branded";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import { useOnInit, useOnRouteChange } from "@hooks/effects/useIoEffect";
import { readOptionString } from "@utils/readOptionString";

const ReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const contentClient = useContentClient();
  const lifecycleClient = useLifecycleClient();
  const {
    document: documentHandle,
    activeSection: activeSectionHandle,
    controller,
  } = useDocumentContext();
  const { appendSection } = controller;
  const { documentId: routeDocumentId } = useParams();
  const loading = useLoadingController();

  const routeDocumentIdOption: OptionTs<DocumentID> = useMemo(
    () =>
      routeDocumentId
        ? Option.some(makeDocumentID(routeDocumentId))
        : Option.none(),
    [routeDocumentId],
  );

  const modalVisible = useIoState(false);
  const newSectionTitle = useIoState("");
  const sectionTitleError = useIoOptionState<string>(Option.none());
  const { selectFile } = useFilePicker();
  const runTaskWithError = useRunTaskWithError();
  const runTaskWithErrorIo = useCallback(
    (task: TaskTs<Ok>) =>
      ioEffect(() => {
        runTaskWithError(task).then(() => ok);
        return ok;
      }),
    [runTaskWithError],
  );
  const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);
  const modalVisibleHandle = useMemo(
    () => ({ read: modalVisible.read, set: modalVisible.set }),
    [modalVisible.read, modalVisible.set],
  );
  const newSectionTitleHandle = useMemo(
    () => ({ read: newSectionTitle.read, set: newSectionTitle.set }),
    [newSectionTitle.read, newSectionTitle.set],
  );
  const sectionTitleErrorHandle = useMemo(
    () => ({
      read: sectionTitleError.read,
      setSome: sectionTitleError.setSome,
      setMaybe: sectionTitleError.setMaybe,
    }),
    [sectionTitleError.read, sectionTitleError.setSome, sectionTitleError.setMaybe],
  );

  const selectUploadPayload = useMemo(
    () =>
      taskMap(selectFile(), (fileOption) =>
        Option.map<
          File,
          UploadSupplementalPayload
        >((file) => ({
          fileName: makeDocumentUploadFileName(file.name),
          file: makeDocumentUploadFile(file.name),
          relation: makeSupplementalRelation("Example of supplemental document"),
        }))(fileOption),
      ),
    [selectFile],
  );

  const showUploadSuccess = useMemo(
    () =>
      ioEffect(() => {
        toast.success("Supplemental document uploaded successfully!");
        return ok;
      }),
    [],
  );

  const downloadHandler = useMemo(
    () =>
      mkBlobHandler((blob: Blob) => (title: string) =>
        ioEffect(() => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${title}.md`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return ok;
        }),
      ),
    [],
  );

  const generateSectionQuestionsFetch: GenerateSectionQuestionsFetch = useMemo(() => {
    const invoke = (documentId: DocumentID, request: GenerateSectionQuestionsRequest) =>
      taskEitherFromEffect(
        contentClient.generateSectionQuestions(documentId, request),
        (reason) => reason as ContentError,
      );

    function fetch(documentId: DocumentID, request: GenerateSectionQuestionsRequest): ReturnType<typeof invoke>;
    function fetch(documentId: DocumentID): (request: GenerateSectionQuestionsRequest) => ReturnType<typeof invoke>;
    function fetch(documentId: DocumentID, request?: GenerateSectionQuestionsRequest) {
      if (request === undefined) {
        return (nextRequest: GenerateSectionQuestionsRequest) => invoke(documentId, nextRequest);
      }
      return invoke(documentId, request);
    }

    return Object.assign(fetch, {
      __brandGenerateSectionQuestionsFetch: "GenerateSectionQuestionsFetch" as const,
    }) as GenerateSectionQuestionsFetch;
  }, [contentClient]);

  const appendSectionRequestFetch: AppendSectionFetch = useMemo(() => {
    const invoke = (documentId: DocumentID, request: AppendSectionRequest) =>
      taskEitherFromEffect(
        contentClient.appendSection(documentId, request),
        (reason) => reason as ContentError,
      );

    function fetch(documentId: DocumentID, request: AppendSectionRequest): ReturnType<typeof invoke>;
    function fetch(documentId: DocumentID): (request: AppendSectionRequest) => ReturnType<typeof invoke>;
    function fetch(documentId: DocumentID, request?: AppendSectionRequest) {
      if (request === undefined) {
        return (nextRequest: AppendSectionRequest) => invoke(documentId, nextRequest);
      }
      return invoke(documentId, request);
    }

    return Object.assign(fetch, {
      __brandAppendSectionFetch: "AppendSectionFetch" as const,
    }) as AppendSectionFetch;
  }, [contentClient]);

  const uploadSupplementalDocumentFetch: UploadSupplementalDocumentFetch = useMemo(() => {
    const invoke = (documentId: DocumentID, request: UploadSupplementalDocumentRequest) =>
      taskEitherFromEffect(
        contentClient.uploadSupplementalDocument(documentId, request),
        (reason) => reason as ContentError,
      );

    function fetch(documentId: DocumentID, request: UploadSupplementalDocumentRequest): ReturnType<typeof invoke>;
    function fetch(documentId: DocumentID): (request: UploadSupplementalDocumentRequest) => ReturnType<typeof invoke>;
    function fetch(documentId: DocumentID, request?: UploadSupplementalDocumentRequest) {
      if (request === undefined) {
        return (nextRequest: UploadSupplementalDocumentRequest) => invoke(documentId, nextRequest);
      }
      return invoke(documentId, request);
    }

    return Object.assign(fetch, {
      __brandUploadSupplementalDocumentFetch: "UploadSupplementalDocumentFetch" as const,
    }) as UploadSupplementalDocumentFetch;
  }, [contentClient]);

  const kernel: DocumentReviewKernel = useMemo(
    () => ({
      loading,
      navigate: mkNavigate((route) =>
        ioEffect(() => {
          navigate(renderRoute(route));
          return ok;
        }),
      ),
      promptRoute: mkRouteBuilder(routeDocumentPrompt),
      reviewRouteWithDocument: mkRouteBuilder(routeReviewWithDocument),
      document: documentHandle,
      activeSection: activeSectionHandle,
      modalVisible: modalVisibleHandle,
      newSectionTitle: newSectionTitleHandle,
      sectionTitleError: sectionTitleErrorHandle,
      appendSectionState: appendSection as StateUpdate<Section>,
      generateSectionQuestions: generateSectionQuestionsFetch,
      appendSection: appendSectionRequestFetch,
      uploadSupplementalDocument: uploadSupplementalDocumentFetch,
      downloadDocumentAsBlob: lifecycleClient.downloadDocumentAsBlob as DownloadDocumentAsBlobFetch,
      fetchDocumentById: lifecycleClient.fetchDocument as FetchDocumentByIdFetch,
      downloadHandler,
      taskRunner,
    }),
    [
      activeSectionHandle,
      appendSection,
      appendSectionRequestFetch,
      documentHandle,
      downloadHandler,
      generateSectionQuestionsFetch,
      lifecycleClient.downloadDocumentAsBlob,
      lifecycleClient.fetchDocument,
      loading,
      navigate,
      modalVisibleHandle,
      newSectionTitleHandle,
      sectionTitleErrorHandle,
      taskRunner,
      uploadSupplementalDocumentFetch,
    ],
  );

  const viewProps = useMemo(
    () => {
      const refineRoute = Option.fold(
        documentHandle.read,
        () => routeDocumentRefine,
        (value) =>
          Option.fold(
            activeSectionHandle.read,
            () => routeDocumentRefine,
            (sectionId) => routeDocumentRefineWithContext(value.id)(sectionId),
          ),
      );
      const finalEditsRoute = Option.fold(
        documentHandle.read,
        () => routeDocumentFinalEdits,
        (value) =>
          Option.fold(
            activeSectionHandle.read,
            () => routeDocumentFinalEdits,
            (sectionId) => routeDocumentFinalEditsWithContext(value.id)(sectionId),
          ),
      );

      return buildDocumentReviewViewProps({
        kernel,
        routeDocumentId: routeDocumentIdOption,
        documentSelectionRoute: routeDocumentSelection,
        refineRoute,
        finalEditsRoute,
        reviewRoute: routeReview,
        selectUploadPayload: selectUploadPayload as SelectUploadPayload,
        uploadSuccessToast: showUploadSuccess as UploadSuccessToast,
      });
    },
    [
      activeSectionHandle.read,
      documentHandle.read,
      kernel,
      routeDocumentIdOption,
      selectUploadPayload,
      showUploadSuccess,
    ],
  );

  // Keep section selection deterministic after route transitions.
  const syncActiveSectionOnRouteChange = useMemo(
    () =>
      ((_routeDocumentId: OptionTs<DocumentID>) =>
        ioEffect(() => {
          Option.fold(
            documentHandle.read,
            () => ok,
            (value) => {
              Option.fold(
                activeSectionHandle.read,
                () => {
                  const sorted = [...value.document_contents].sort(
                    (a, b) => a.metadata.index - b.metadata.index,
                  );
                  const started = sorted.find(
                    (section) => readOptionString(section.section_contents).trim().length > 0,
                  );
                  const fallback = sorted[0];
                  const target = started ?? fallback;
                  if (target) {
                    activeSectionHandle.setSome(target.metadata.id);
                  }
                },
                () => ok,
              );
              return ok;
            },
          );
          return ok;
        })) as EmitOnRouteChange<OptionTs<DocumentID>>,
    [activeSectionHandle, documentHandle.read],
  );
  useOnRouteChange(routeDocumentIdOption, syncActiveSectionOnRouteChange);

  useOnInit(viewProps.onInit);

  return <ReviewView {...viewProps} />;
};

export default ReviewPage;
